import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import globalNotification from 'live/services/global-notification'
import { BodyRow, HeadRow, StyledCardContainer, Table } from '../styles/styles'
import { OrderingHeader } from 'live/components/ui/List'
import dateService from 'live/services/date'
import { canManageMetadata } from '../../permission/Permissions'
import { CSVLink } from 'react-csv'

interface Props {
    selected: {
        asset: {
            id: string
            name: string
        }
    }
}

interface Collection {
    name: string
    timestamp?: number
    length: number
}

const UpdateAsset: React.FC<Props> = ({ selected }: Props) => {
    const [collections, setCollections] = useState<Collection[]>([])
    const [collectionData, setCollectionData] = useState<[]>([])
    const [collectionName, setCollectionName] = useState<string>('')
    const [collapsed, setCollapsed] = useState(true)

    function toKiloBytes(bytes: number): string {
        const kilobytes = bytes / 1024

        return `${kilobytes.toFixed(2)} KB`
    }

    useEffect(() => {
        const url = `services/plugin-asset-metadata/asset/collections?wellboreId=${selected.asset.id}`

        fetch(url)
            .then(result => {
                if (result.ok) {
                    return result.json()
                } else {
                    throw new Error()
                }
            })

            .then(json => setCollections(json))
            .catch(e => globalNotification.error(`An error occurred when fetching collections data: ${e.message}`))
    }, [selected.asset.id])

    const handleUpdate = (wellboreId: string): void => {
        fetch(`services/plugin-asset-metadata/asset/updateAsset?wellboreId=${wellboreId}`, {
            method: 'GET'
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok')
                }
            })
            .then(() =>
                globalNotification.success(
                    `Update for wellbore ${selected.asset.name} requested, check intervention in a few`
                )
            )
            .catch(e => globalNotification.error(`An error occurred when requestiong wellbore update: ${e.message}`))
    }

    const handleGetCollectionData = (wellboreId: string, eventType: string): void => {
        const url = `services/plugin-asset-metadata/asset/getCollectionData?wellboreId=${selected.asset.id}&eventType=${eventType}`

        fetch(url)
            .then(result => {
                if (result.ok) {
                    return result.json()
                } else {
                    throw new Error()
                }
            })

            .then(json => setCollectionData(json))
            .then(() => setCollectionName(eventType))
            .catch(e => globalNotification.error(`An error occurred when getting ${eventType} collection data: ${e.message}`))
    }

    const handleDownloadJson = (eventType: string): void => {
        const dataStr = JSON.stringify(collectionData)
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)
        const downloadNode = downloadLink.current
        downloadNode.setAttribute('href', dataUri)
        downloadNode.setAttribute('download', `${eventType}.json`)
        downloadNode.click()
    }

    const downloadLink = useRef()
    const fromNow = collections.reduce(
        (oldest, item) => (item.timestamp > oldest ? item?.timestamp : oldest),
        collections[0]?.timestamp
    )

    return (
        <StyledCardContainer
            title={`Metadata`}
            subtitle={fromNow ? ` - Updated on ${dateService.fromNow(fromNow)}` : ` - Never fetched`}
            collapsed={collapsed}
            renderControls={() => {
                return (
                    <>
                        {canManageMetadata() && (
                            <Button onClick={() => handleUpdate(selected.asset.id)}>
                                <i className="far fa-sync-alt" />
                            </Button>
                        )}
                        <Button onClick={() => setCollapsed(!collapsed)}>
                            <i className={collapsed ? 'far fa-angle-up' : 'far fa-angle-down'} />
                        </Button>
                    </>
                )
            }}
        >
            <Table>
                <HeadRow>
                    <OrderingHeader>Collection Name</OrderingHeader>
                    <OrderingHeader>Size on Disk</OrderingHeader>
                    <OrderingHeader>Updated On</OrderingHeader>
                    <OrderingHeader></OrderingHeader>
                    <OrderingHeader>Action</OrderingHeader>
                    <OrderingHeader></OrderingHeader>
                    <OrderingHeader></OrderingHeader>
                </HeadRow>
                {collections.map(item => (
                    <BodyRow key={`${item.name}`}>
                        <div>
                            <strong>{item.name}</strong>
                        </div>
                        <div>{toKiloBytes(item.length)}</div>
                        <div>{item.timestamp && dateService.formatDateTime(item.timestamp)}</div>
                        <div>{item.timestamp && dateService.fromNow(item.timestamp)}</div>
                        <div>
                            {canManageMetadata() && (
                                <Button
                                    id={`get-${item.name}`}
                                    variant={'default'}
                                    onClick={() => handleGetCollectionData(selected.asset.id, item.name)}
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    Get Data
                                </Button>
                            )}
                        </div>
                        <div>
                            {collectionData.length > 0 && collectionName === item.name && (
                                <CSVLink
                                    id={`csv-link-${item.name}`}
                                    data={collectionData}
                                    filename={`${item.name}.csv`}
                                    className={'btn btn-default'}
                                >
                                    CSV
                                    <i className={'far fa-download'} style={{ marginLeft: '10px' }} />
                                </CSVLink>
                            )}
                        </div>
                        <div>
                            {collectionData.length > 0 && collectionName === item.name && (
                                <>
                                    <Button id={`json-${item.name}`} onClick={() => handleDownloadJson(item.name)}>
                                        JSON
                                        <i className={'far fa-download'} style={{ marginLeft: '10px' }} />
                                    </Button>
                                    <a ref={downloadLink} style={{ display: 'none' }} />
                                </>
                            )}
                        </div>
                    </BodyRow>
                ))}
            </Table>
        </StyledCardContainer>
    )
}

export default UpdateAsset
